import moment from 'moment-timezone';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getSuggestedTimes } from '@/consultation';

import { FLOW_CONFIG } from '../constants';
import { Expert, PaymentProgress, SchedulingData, Screen } from '../types';
import { trackSMBSchedulingCompleted } from '../utils/smbAnalytics';

interface UseSignupFlowProps {
  expert: Expert;
  screen: Screen;
  onComplete: () => void;
}

export function useSignupFlow({
  expert,
  screen,
  onComplete: _onComplete,
}: UseSignupFlowProps): ReturnType<typeof useSignupFlowInternal> {
  return useSignupFlowInternal({ expert, screen });
}

function useSignupFlowInternal({ expert, screen }: { expert: Expert; screen: Screen }) {
  // Stripe payment state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentProgress, setPaymentProgress] = useState<PaymentProgress>({
    confirmedClientSecret: null,
    signupResult: null,
    retryBlockedReason: null,
  });

  // Scheduling state
  const [viewerTimezone] = useState(() => moment.tz.guess());
  const [selectedDuration, setSelectedDuration] = useState<number>(
    FLOW_CONFIG.DEFAULT_DURATION_MINUTES
  );
  const [schedulingData, setSchedulingData] = useState<SchedulingData | null>(null);

  // Track time when scheduling screen is entered
  const schedulingStartTime = useRef<Date>(new Date());

  // Reset scheduling start time when returning to booking screen
  useEffect(() => {
    if (screen === 'booking') {
      schedulingStartTime.current = new Date();
    }
  }, [screen]);

  // Scheduling form initial values - preserve data when navigating back
  const schedulingInitialValues = useMemo(
    () => ({
      dates:
        schedulingData?.dates ||
        getSuggestedTimes({
          t1: viewerTimezone,
          t2: expert.timezone || viewerTimezone,
          hourOffset: FLOW_CONFIG.SUGGESTED_TIME_HOUR_OFFSET,
          hourInterval: FLOW_CONFIG.SUGGESTED_TIME_INTERVAL,
          skipWeekends: true,
        }),
      duration: schedulingData?.duration || '1h',
      description: schedulingData?.description || '',
    }),
    [viewerTimezone, expert.timezone, schedulingData]
  );

  // Viewer object for ConsultationDates component
  const viewer = useMemo(
    () => ({
      timezone: viewerTimezone,
    }),
    [viewerTimezone]
  );

  // Handle scheduling form continue
  const handleSchedulingContinue = (formValues?: {
    duration?: string;
    dates?: string[];
    description?: string;
  }) => {
    if (formValues?.duration) {
      const durationStr = formValues.duration;
      let minutes: number = FLOW_CONFIG.DEFAULT_DURATION_MINUTES;
      if (durationStr.endsWith('m')) {
        minutes = parseInt(durationStr, 10);
      } else if (durationStr.endsWith('h')) {
        minutes = parseFloat(durationStr) * 60;
      } else {
        console.warn(
          `Unrecognized duration format: "${durationStr}". Defaulting to ${FLOW_CONFIG.DEFAULT_DURATION_MINUTES} minutes.`
        );
      }
      setSelectedDuration(minutes);

      setSchedulingData({
        dates: formValues.dates || [],
        duration: formValues.duration,
        description: formValues.description || '',
      });

      // Track scheduling completion
      const durationOnScreen = Math.round(
        (new Date().getTime() - schedulingStartTime.current.getTime()) / 1000
      );
      const hasDescription = !!(formValues.description && formValues.description.trim());
      trackSMBSchedulingCompleted(durationOnScreen, hasDescription);
    }
  };

  const handlePaymentSuccess = () => {
    setIsProcessingPayment(false);
  };

  const handlePaymentError = (message: string) => {
    setPaymentError(message);
    setIsProcessingPayment(false);
  };

  const updatePaymentProgress = (update: Partial<PaymentProgress>) => {
    setPaymentProgress((current) => ({ ...current, ...update }));
  };

  return {
    // Payment state
    isProcessingPayment,
    setIsProcessingPayment,
    paymentProgress,
    updatePaymentProgress,
    paymentError,

    // Scheduling state
    viewerTimezone,
    selectedDuration,
    schedulingData,
    schedulingInitialValues,
    viewer,

    // Handlers
    handleSchedulingContinue,
    handlePaymentSuccess,
    handlePaymentError,
  };
}
